import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LuChevronLeft, LuChevronRight, LuPause, LuPlay } from 'react-icons/lu';

import "./TestimonialCarousel.scss";
import GoogleReviewCard from "./GoogleReviewCard";
import useSlidesToShow from "./useSlideToShow"

export const testimonials = [
    {
        name: "Hanz W",
        testimonial:
            "We highly appreciate Arkham Law’s efficiency, timeliness and responsiveness on our real-estate deals. Their proactive approach kept everything smooth."
    },
    {
        name: "Emily K",
        testimonial:
            "Wonderful experience with Arkham Law. Wesley never rushed us and explained every document in plain language when we needed clarification."
    },
    {
        name: "Albert M",
        testimonial:
            "Wesley handled my property sale with professionalism and constant responsiveness. The whole process was seamless and stress-free."
    },
    {
        name: "Roxana S",
        testimonial:
            "Wesley assisted with my condo sale contract. He was friendly, efficient and answered every question, making the process easy."
    },
    {
        name: "Tuyet T",
        testimonial:
            "After completing my PR journey, I can say Wesley was incredibly attentive and knowledgeable. I highly recommend his team for immigration matters."
    },
    {
        name: "Martin D",
        testimonial:
            "Great experience with Arkham. The team was efficient, communicative and detail-oriented—even with my unconventional case."
    },
    {
        name: "Amanda K",
        testimonial:
            "I’ve used Wesley Wong on multiple real-estate deals. He’s quick, easy to work with and happy to accommodate virtual meetings for clients."
    },
    {
        name: "Jon M",
        testimonial:
            "Arkham Law handled my house sale smoothly. Wesley, Viann and Fion kept me reassured and were always available to address concerns."
    }
];


const TestimonialCarousel = () => {
    const slidesToShow = useSlidesToShow();
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(1);
    const reduceMotion = useReducedMotion();
    const [autoPlayChoice, setAutoPlayChoice] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const swipeStart = useRef(null);
    const wantsAutoPlay = autoPlayChoice ?? !reduceMotion;
    const isRotating = wantsAutoPlay && !isHovered && !isFocused;

    useEffect(() => {
        if (!isRotating) return;
        const id = setInterval(() => {
            setDirection(1);
            setIndex(i => (i + slidesToShow) % testimonials.length);
        }, 8_000);
        return () => clearInterval(id);
    }, [index, slidesToShow, isRotating]);

    const move = step => {
        setDirection(step);
        setIndex(i => (i + step * slidesToShow + testimonials.length) % testimonials.length);
    };

    const onPointerUp = event => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (!start) return;
        const dx = event.clientX - start.x;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(event.clientY - start.y)) move(dx < 0 ? 1 : -1);
    };

    const visible = useMemo(
        () =>
            Array.from({ length: slidesToShow }, (_, k) =>
                testimonials[(index + k) % testimonials.length]
            ),
        [index, slidesToShow]
    );

    return (
        <section className="testimonial-carousel" aria-label="Client testimonials" aria-roledescription="carousel"
                 onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
                 onFocusCapture={() => setIsFocused(true)}
                 onBlurCapture={event => {
                     if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false);
                 }}>
            <div className="testimonial-content" aria-live={isRotating ? 'off' : 'polite'} aria-atomic="true"
                 onPointerDown={event => {
                     swipeStart.current = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null;
                 }}
                 onPointerUp={onPointerUp}
                 onPointerCancel={() => { swipeStart.current = null; }}>
                <motion.div
                    key={`${index}-${slidesToShow}`}
                    initial={reduceMotion ? false : { opacity: 0, x: 12 * direction }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.3 }}
                    className="testimonial-row"
                >
                    {visible.map(({ name, testimonial }, offset) => (
                        <div className="testimonial-slide" key={name} role="group" aria-roledescription="slide"
                             aria-label={`Review ${(index + offset) % testimonials.length + 1} of ${testimonials.length}`}>
                            <GoogleReviewCard name={name} review={testimonial} />
                        </div>
                    ))}
                </motion.div>
            </div>
            <div className="testimonial-controls" role="group" aria-label="Testimonial controls">
                <button className="carousel-btn" type="button" aria-label="Previous reviews" onClick={() => move(-1)}>
                    <LuChevronLeft aria-hidden="true" />
                </button>
                <button className="carousel-btn" type="button"
                        aria-label={wantsAutoPlay ? 'Pause automatic reviews' : 'Play automatic reviews'}
                        onClick={() => setAutoPlayChoice(!wantsAutoPlay)}>
                    {wantsAutoPlay ? <LuPause aria-hidden="true" /> : <LuPlay aria-hidden="true" />}
                </button>
                <button className="carousel-btn" type="button" aria-label="Next reviews" onClick={() => move(1)}>
                    <LuChevronRight aria-hidden="true" />
                </button>
            </div>
        </section>
    );
};

export default TestimonialCarousel;
